import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search,
  Filter,
  Edit, 
  Trash2,
  Plus,
  AlertCircle,
  Loader2,
  Palette,
  Shield
} from 'lucide-react';
import { 
  getActivityTypes, 
  deleteActivityType,
  ActivityType 
} from '../services/activityApi';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';

export function ActivityTypesListPage() {
  const navigate = useNavigate();
  
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filteredTypes, setFilteredTypes] = useState<ActivityType[]>([]);
  const visibleTypeIds = useMemo(() => filteredTypes.map((t) => t.id), [filteredTypes]);
  const {
    selectedIds: selectedTypeIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleTypeIds);

  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchActivityTypes();
  }, []);

  useEffect(() => {
    // Filter activity types based on search
    if (!search.trim()) {
      setFilteredTypes(activityTypes);
    } else {
      const searchLower = search.toLowerCase();
      setFilteredTypes(
        activityTypes.filter(type => 
          type.name.toLowerCase().includes(searchLower) ||
          type.description?.toLowerCase().includes(searchLower)
        )
      );
    }
  }, [search, activityTypes]);

  const fetchActivityTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const types = await getActivityTypes();
      setActivityTypes(types);
    } catch (error: any) {
      console.error('Fetch activity types error:', error);
      setError(error.message || 'Failed to load activity types');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (typeId: string) => {
    navigate(`/planning/activity-types/${typeId}/edit`);
  };

  const handleCreate = () => {
    navigate('/planning/activity-types/create');
  };

  const handleDelete = async (typeId: string, typeName: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le type d'activité "${typeName}" ?`)) {
      return;
    }

    try {
      await deleteActivityType(typeId);
      await fetchActivityTypes();
    } catch (error: any) {
      alert(error.message || 'Failed to delete activity type');
    }
  };

  const openBulkDeleteConfirm = () => {
    if (selectedTypeIds.size === 0) return;
    setBulkDeleteConfirm({ isOpen: true, ids: Array.from(selectedTypeIds) });
  };
  const cancelBulkDelete = () => setBulkDeleteConfirm({ isOpen: false, ids: [] });
  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.ids.length === 0) return;
    try {
      setBulkDeleting(true);
      const failed: string[] = [];
      for (const id of bulkDeleteConfirm.ids) {
        try {
          await deleteActivityType(id);
        } catch {
          failed.push(id);
        }
      }
      setBulkDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await fetchActivityTypes();
      if (failed.length > 0) {
        alert(`${failed.length} type(s) n’ont pas pu être supprimés.`);
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const getVisibilityBadge = (visibility: string) => {
    const colorMap: Record<string, string> = {
      'INTERNAL': 'bg-blue-100 text-blue-800',
      'FRONTOFFICE': 'bg-green-100 text-green-800',
      'PRIVATE': 'bg-gray-100 text-gray-800'
    };

    const labelMap: Record<string, string> = {
      'INTERNAL': 'Interne',
      'FRONTOFFICE': 'Public',
      'PRIVATE': 'Privé'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        colorMap[visibility] || 'bg-gray-100 text-gray-800'
      }`}>
        {labelMap[visibility] || visibility}
      </span>
    );
  };

  if (loading && activityTypes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des types d'activités...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Types d'Activités</span>
            </h1>
            <p className="text-textSecondary mt-1">
              {filteredTypes.length} type{filteredTypes.length !== 1 ? 's' : ''} d'activité
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un Type</span>
          </button>
          {selectedCount > 0 && (
            <button
              onClick={openBulkDeleteConfirm}
              disabled={bulkDeleting}
              className="ml-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
              title="Supprimer les types sélectionnés"
            >
              <Trash2 className="w-4 h-4" />
              <span>Supprimer ({selectedCount})</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un type d'activité..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => setSearch('')}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchActivityTypes}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Activity Types Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={filteredTypes.length === 0 || bulkDeleting}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    aria-label="Sélectionner tous les types de la liste"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Shield className="w-4 h-4" />
                    <span>Nom</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Palette className="w-4 h-4" />
                    <span>Couleur</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bloque les réservations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visibilité
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTypes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    {search ? 'Aucun type d\'activité trouvé' : 'Aucun type d\'activité'}
                  </td>
                </tr>
              ) : (
                filteredTypes.map((type) => (
                  <tr key={type.id} className="group hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedTypeIds.has(type.id)}
                        disabled={bulkDeleting}
                        onChange={() => toggleSelect(type.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        aria-label={`Sélectionner le type ${type.name}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">{type.name}</div>
                        {type.isDefault && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700">
                            Par défaut
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-md truncate">
                        {type.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: type.defaultColor }}
                        />
                        <span className="text-sm text-gray-600">{type.defaultColor}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        type.isBlocking 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {type.isBlocking ? 'Oui' : 'Non'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getVisibilityBadge(type.defaultVisibility)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(type.id)}
                          className="text-primary-600 hover:text-primary-900 p-1"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(type.id, type.name)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>
      </div>

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.ids.length}</strong> type{bulkDeleteConfirm.ids.length !== 1 ? 's' : ''} ?
              Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

